import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box, Chip } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import MicrospecDataTable, { MicrospecDataTableColumn } from "../../../../MicrospecDynamicUI/Components/DataTable/MicrospecFirestoreDataTable";
import { useMicrospecDynamicUIContext } from "../../../../MicrospecDynamicUI/GlobalProvider";
import { deleteDocumentById } from "../../../../MicrospecDynamicUI/Helpers/dataUtils";
import { useMicrospecThemeLanguageContext } from "../../../../MicrospecDynamicUI/Themes";
import { PRODUCTS_COLLECTION } from "../../../constants/collectionPaths";

const ProductsManagementPage: React.FC = () => {
    const navigate = useNavigate();
    const { translate } = useMicrospecThemeLanguageContext().localizationProvider;
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const db = cloudProvider.firestore?.instance;
    const t = translate;
    const [refreshKey, setRefreshKey] = useState(0);

    const columns: MicrospecDataTableColumn[] = [
        {
            field: 'images',
            headerName: '',
            sortable: false,
            minWidth: 70,
            align: 'center',
            render: (value: string[]) => value?.[0] ? (
                <Box component="img" src={value[0]} alt="product" sx={{ width: 48, height: 36, objectFit: 'cover', borderRadius: 1 }} />
            ) : null,
        },
        {
            field: 'name',
            headerName: t('product.name'),
            sortable: true,
            minWidth: 200,
            align: 'left',
            render: (value: string | { [locale: string]: string }) => typeof value === 'string' ? value : translate(value),
        },
        {
            field: 'price',
            headerName: t('product.price'),
            sortable: true,
            minWidth: 120,
            align: 'center',
            render: (value: number, row: any) => {
                const label = row.quantity_label ? translate(row.quantity_label) : null;
                return `₪${value?.toFixed(2) ?? '0.00'}${label ? ` / ${label}` : ''}`;
            },
        },
        {
            field: 'is_published',
            headerName: t('product.published'),
            sortable: true,
            minWidth: 120,
            align: 'center',
            render: (value: boolean) => (
                <Chip label={value ? t('category.published') : t('category.draft')} color={value ? 'success' : 'default'} size="small" variant="outlined" />
            ),
        },
        {
            field: 'is_available',
            headerName: t('product.available'),
            sortable: true,
            minWidth: 120,
            align: 'center',
            render: (value: boolean) => (
                <Chip label={value ? t('product.inStock') : t('product.outOfStock')} color={value ? 'info' : 'warning'} size="small" variant="outlined" />
            ),
        },
    ];

    const handleDelete = async (row: any) => {
        if (!db) return;
        if (window.confirm(t('product.confirmDelete'))) {
            try {
                await deleteDocumentById(db, PRODUCTS_COLLECTION, row.id);
                setRefreshKey(prev => prev + 1);
            } catch (error: unknown) {
                console.error('Error deleting product:', error);
                alert(t('product.deleteFailed'));
            }
        }
    };

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            <MicrospecDataTable
                key={refreshKey}
                collection={PRODUCTS_COLLECTION}
                columns={columns}
                title={t('common.products')}
                subtitle={t('product.manageDescription')}
                searchable={true}
                exportable={true}
                refreshable={true}
                pageSize={25}
                maxHeight={700}
                onRowClick={(row) => navigate(`/manager/products/update/${row.id}`)}
                actions={[
                    { icon: <EditIcon />, label: t('common.edit'), onClick: (row) => navigate(`/manager/products/update/${row.id}`) },
                    { icon: <DeleteIcon />, label: t('common.delete'), onClick: handleDelete },
                ]}
                globalActions={[
                    { icon: <AddIcon />, label: t('common.addProduct'), onClick: () => navigate('/manager/products/add'), variant: 'contained', color: 'primary' },
                ]}
            />
        </Box>
    );
};

export default ProductsManagementPage;
